/* Exercise -> muscles and weekly volume: the muscle resolver, canonical names
 * (state.exerciseAliases), weekly hard sets per muscle, planned weekly sets,
 * volume landmarks, same-muscle alternatives and rename suggestions. Pure
 * functions over muscleMap.json; the Python twin is src/fitlogic/muscles.py,
 * which reads the same map, and both are pinned by vectors/muscles.json.
 *
 * Volume groups are MUSCLE_GROUPS with Shoulders split into Front, Side and
 * Rear delts. A primary muscle earns a set 1.0, each secondary 0.5. Cardio
 * resolves but never counts as volume. */

import MAP from './muscleMap.json';

const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const DELTS = ['Front delts', 'Side delts', 'Rear delts'];
const VALID_GROUPS = [...MAP.groups, 'Cardio'];
const EQUIPMENT = new Map([
  ['machine', 'machine'], ['smith', 'machine'], ['cable', 'cable'], ['dumbbell', 'dumbbell'],
  ['barbell', 'barbell'], ['plate', 'plate'], ['bodyweight', 'bodyweight'], ['kettlebell', 'kettlebell'],
  ['band', 'band'],
]);
// 'smith' stays in the equipment-stripped key: a Smith squat is not a back squat.
const STRIPPED = new Set([...EQUIPMENT.keys()].filter(w => w !== 'smith'));
const SYNONYMS = new Map([
  ['db', 'dumbbell'], ['bb', 'barbell'], ['flye', 'fly'], ['flie', 'fly'], ['ups', 'up'], ['pullup', 'pull up'],
  ['chinup', 'chin up'], ['pushup', 'push up'], ['situp', 'sit up'], ['skullcrusher', 'skull crusher'],
]);
const SIMILAR = 0.85;
const LOAD_BAND = 0.35;

/** Volume groups in display order (no Cardio). */
export const VOLUME_GROUPS = MAP.groups.slice();

function roundTo(x, f) {
  return Math.round(x * f) / f || 0;
}

function isObj(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function isNum(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function own(obj, key) {
  return isObj(obj) && Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : undefined;
}

function dayNumber(key) {
  if (typeof key !== 'string' || !DATE_RE.test(key)) return null;
  const [y, m, d] = key.split('-').map(Number);
  const back = new Date(Date.UTC(y, m - 1, d));
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return Math.round(back.getTime() / 86400000);
}

function singular(w) {
  if (w.length <= 3) return w;
  if (/(?:ches|shes|xes|sses)$/.test(w)) return w.slice(0, -2);
  if (w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
  return w;
}

/* Lowercase words with punctuation and hyphens as separators, plurals
 * folded and a few spellings unified ('db' -> dumbbell, 'pullup' -> pull up).
 * dropBrackets removes "(...)" and "[...]" with their content. */
function words(name, dropBrackets) {
  if (typeof name !== 'string') return [];
  let s = name.toLowerCase();
  if (dropBrackets) s = s.replace(/\([^)]*\)|\[[^\]]*\]/g, ' ');
  const out = [];
  for (const raw of s.split(/[^a-z0-9]+/)) {
    if (!raw) continue;
    const w = singular(raw);
    out.push(...(SYNONYMS.get(w) || w).split(' '));
  }
  return out;
}

function softKey(name) {
  return words(name, false).join(' ');
}

function strippedKey(name) {
  const w = words(name, false);
  const out = [];
  for (let i = 0; i < w.length; i += 1) {
    if (w[i] === 'per' && w[i + 1] === 'arm') { i += 1; continue; }
    if (!STRIPPED.has(w[i])) out.push(w[i]);
  }
  return out;
}

function equipmentWord(name) {
  for (const w of words(name, false)) if (EQUIPMENT.has(w)) return EQUIPMENT.get(w);
  return null;
}

/**
 * The identity key used to spot renames: lowercase, bracketed parts removed,
 * punctuation and hyphens as spaces, plural 's' folded, 'db' -> dumbbell.
 * @param {string} name
 * @returns {string} e.g. 'Preacher Curl (machine, per arm)' -> 'preacher curl'
 */
export function nameKey(name) {
  return words(name, true).join(' ');
}

/* Lookup tables, built once. Library entries win over extras on a clash. */
const ENTRIES = [...MAP.library, ...MAP.extras];
const BY_NAME = new Map();
const BY_SOFT = new Map();
const BY_STRIPPED = new Map();
ENTRIES.forEach(e => {
  if (!BY_NAME.has(e.name)) BY_NAME.set(e.name, e);
  const soft = softKey(e.name);
  if (!BY_SOFT.has(soft)) BY_SOFT.set(soft, e);
  const stripped = strippedKey(e.name).join(' ');
  if (!BY_STRIPPED.has(stripped)) BY_STRIPPED.set(stripped, []);
  BY_STRIPPED.get(stripped).push(e);
});
const ALIAS_BY_SOFT = new Map();
Object.keys(MAP.aliases).forEach(k => {
  const soft = softKey(k);
  if (!ALIAS_BY_SOFT.has(soft)) ALIAS_BY_SOFT.set(soft, MAP.aliases[k]);
});

/* The library (or extras) entry a name stands for: exact, normalised, the
 * map's aliases, then the equipment-stripped name. The stripped match needs
 * two words left, so a bare 'Curl' isn't read as 'Cable Curls' (forearms). */
function findEntry(name) {
  if (typeof name !== 'string') return null;
  if (BY_NAME.has(name)) return { entry: BY_NAME.get(name), via: 'library' };
  const soft = softKey(name);
  if (!soft) return null;
  if (BY_SOFT.has(soft)) return { entry: BY_SOFT.get(soft), via: 'library' };
  const target = own(MAP.aliases, name) || ALIAS_BY_SOFT.get(soft);
  if (target && BY_NAME.has(target)) return { entry: BY_NAME.get(target), via: 'alias' };
  const stripped = strippedKey(name);
  if (stripped.length < 2) return null;
  const candidates = BY_STRIPPED.get(stripped.join(' '));
  if (!candidates) return null;
  const equipment = equipmentWord(name);
  return { entry: candidates.find(e => e.equipment === equipment) || candidates[0], via: 'library' };
}

function keywordRule(name) {
  const padded = ` ${softKey(name)} `;
  if (padded === '  ') return null;
  return MAP.keywords.find(rule => rule.match.some(kw => padded.includes(` ${kw} `))) || null;
}

/* A group name as stored (any case) -> the volume group. 'Shoulders' is split
 * by the exercise name (laterals -> side, rear/face pull -> rear), presses and
 * everything else -> front delts. */
function toGroup(value, name) {
  if (typeof value !== 'string') return null;
  const v = value.trim().toLowerCase();
  if (v === 'shoulders' || v === 'shoulder' || v === 'delts') {
    const rule = keywordRule(name);
    return rule && DELTS.includes(rule.primary) ? rule.primary : 'Front delts';
  }
  return VALID_GROUPS.find(g => g.toLowerCase() === v) || null;
}

function credit(primary, secondary, name) {
  const out = [];
  (Array.isArray(secondary) ? secondary : []).forEach(s => {
    const g = toGroup(s, name);
    if (g && g !== primary && !out.includes(g)) out.push(g);
  });
  return out;
}

function entryMuscles(entry) {
  const ov = own(MAP.overrides, entry.name);
  if (ov) return { primary: ov.primary, secondary: ov.secondary.slice() };
  return { primary: toGroup(entry.muscle, entry.name), secondary: [] };
}

/**
 * Which muscles an exercise trains. Order: exerciseMeta[name] (a known
 * primary group; 'Shoulders' is split into a delt by the name), then the
 * library with the map's overrides (exact, normalised, the map's aliases for
 * default-plan names, equipment-stripped), then keyword rules.
 * @param {string} name exercise name as logged
 * @param {object} [exerciseMeta] state.exerciseMeta ({ [name]: { primary, secondary } })
 * @returns {{primary:string, secondary:string[], source:'meta'|'library'|'alias'|'keyword',
 *   match:string|null}|null} match is the library (or extra) name the input
 *   stands for, whatever decided the muscles; null when nothing resolves
 */
export function resolveMuscles(name, exerciseMeta) {
  if (typeof name !== 'string' || !name.trim()) return null;
  const hit = findEntry(name);
  const match = hit ? hit.entry.name : null;
  const meta = own(exerciseMeta, name);
  if (isObj(meta)) {
    const primary = toGroup(meta.primary, name);
    if (primary) return { primary, secondary: credit(primary, meta.secondary, name), source: 'meta', match };
  }
  if (hit) {
    const m = entryMuscles(hit.entry);
    if (m.primary) return { primary: m.primary, secondary: credit(m.primary, m.secondary, name), source: hit.via, match };
  }
  const rule = keywordRule(name);
  if (!rule) return null;
  return { primary: rule.primary, secondary: credit(rule.primary, rule.secondary, name), source: 'keyword', match: null };
}

/**
 * Follows state.exerciseAliases ({ old: new }) to the current name. A cycle
 * (a -> b -> a) settles on its alphabetically first member, so every name
 * in it folds the same way.
 * @param {string} name
 * @param {object} [aliases] state.exerciseAliases
 * @returns {string} the input unchanged when it has no alias
 */
export function canonicalName(name, aliases) {
  if (typeof name !== 'string' || !isObj(aliases)) return name;
  const seen = [name];
  let cur = name;
  for (;;) {
    const next = own(aliases, cur);
    if (typeof next !== 'string' || !next || next === cur) return cur;
    const at = seen.indexOf(next);
    if (at >= 0) return seen.slice(at).reduce((a, b) => (b < a ? b : a));
    seen.push(next);
    cur = next;
  }
}

/**
 * A hard (working) set: done, reps > 0 and not marked as far from failure
 * (rir 5 = "4+"). A missing or null rir counts, so old sets count.
 * @param {object} set logs[date][name][i]
 * @returns {boolean}
 */
export function isHardSet(set) {
  if (!isObj(set) || !set.done) return false;
  if (!isNum(set.reps) || set.reps <= 0) return false;
  return !isNum(set.rir) || set.rir < 5;
}

function addTo(map, key, n) {
  map.set(key, (map.get(key) || 0) + n);
}

function rounded(map, scale) {
  return Object.fromEntries([...map].map(([k, v]) => [k, roundTo(v * scale, 10)]));
}

/* Muscles of a logged name after alias folding. An exerciseMeta entry on
 * either name beats the library, and the canonical name beats the old one. */
function musclesOf(name, canon, meta) {
  const m = resolveMuscles(canon, meta);
  if (canon === name || (m && m.source === 'meta')) return m;
  const old = resolveMuscles(name, meta);
  return old && (old.source === 'meta' || !m) ? old : m;
}

/* Credits n sets of one exercise and returns its canonical name, or null for
 * cardio (not volume). */
function creditSets(name, n, state, byMuscle, unmapped) {
  const canon = canonicalName(name, state.exerciseAliases);
  const m = musclesOf(name, canon, state.exerciseMeta);
  if (m && m.primary === 'Cardio') return null;
  if (!m) addTo(unmapped, canon, n);
  else {
    addTo(byMuscle, m.primary, n);
    m.secondary.forEach(g => addTo(byMuscle, g, n * 0.5));
  }
  return canon;
}

/**
 * Hard sets per muscle for the 7 days weekStart..weekStart+6 (pass a Monday
 * for a Mon-Sun week). Names fold through state.exerciseAliases first.
 * @param {object} state needs logs; reads exerciseAliases, exerciseMeta
 * @param {string} weekStart YYYY-MM-DD
 * @returns {{byMuscle:Object<string,number>, unmapped:Object<string,number>,
 *   byExercise:Object<string,number>, total:number}} byMuscle has credited
 *   sets (primary 1, secondary 0.5) for muscles with any; unmapped and
 *   byExercise count hard sets per canonical name; total = hard sets
 *   (cardio left out everywhere)
 */
export function weeklyHardSets(state, weekStart) {
  const byMuscle = new Map();
  const unmapped = new Map();
  const byExercise = new Map();
  let total = 0;
  const start = dayNumber(weekStart);
  const logs = isObj(state) && isObj(state.logs) ? state.logs : null;
  if (start !== null && logs) {
    for (let n = start; n < start + 7; n += 1) {
      const day = own(logs, new Date(n * 86400000).toISOString().slice(0, 10));
      if (!isObj(day)) continue;
      for (const name of Object.keys(day)) {
        const sets = Array.isArray(day[name]) ? day[name].filter(isHardSet).length : 0;
        const canon = sets ? creditSets(name, sets, state, byMuscle, unmapped) : null;
        if (canon === null) continue;
        addTo(byExercise, canon, sets);
        total += sets;
      }
    }
  }
  return { byMuscle: rounded(byMuscle, 1), unmapped: rounded(unmapped, 1), byExercise: rounded(byExercise, 1), total };
}

/**
 * Planned sets per muscle per 7 days: plan sets x credit x 7 / plan.length
 * (an 8-day cycle -> x0.875). Uses the plan's own set counts, not the per-day
 * setCounts overrides; rest days are skipped and a missing sets count is 3,
 * as hydrateState does.
 * @param {object} state needs plan; reads exerciseAliases, exerciseMeta
 * @returns {{byMuscle:Object<string,number>, unmapped:Object<string,number>, total:number}}
 *   all rounded to 0.1
 */
export function plannedWeeklySets(state) {
  const byMuscle = new Map();
  const unmapped = new Map();
  let total = 0;
  const plan = isObj(state) && Array.isArray(state.plan) ? state.plan : [];
  plan.forEach(day => {
    if (!isObj(day) || day.isRest || !Array.isArray(day.exercises)) return;
    day.exercises.forEach(ex => {
      if (!isObj(ex) || typeof ex.name !== 'string' || !ex.name.trim()) return;
      const sets = isNum(ex.sets) && ex.sets > 0 ? ex.sets : 3;
      if (creditSets(ex.name, sets, state, byMuscle, unmapped) !== null) total += sets;
    });
  });
  const scale = plan.length ? 7 / plan.length : 0;
  return { byMuscle: rounded(byMuscle, scale), unmapped: rounded(unmapped, scale), total: roundTo(total * scale, 10) };
}

/**
 * Weekly-set landmarks per volume group, the map's rough defaults with
 * state.profile.volumeLandmarks[muscle] fields (numbers >= 0) on top.
 * @param {object} [state]
 * @returns {Object<string, {mev:number, mavLow:number, mavHigh:number, mrv:number}>}
 */
export function volumeLandmarks(state) {
  const custom = isObj(state) && isObj(state.profile) ? state.profile.volumeLandmarks : null;
  const out = {};
  Object.keys(MAP.landmarks).forEach(muscle => {
    const lm = { ...MAP.landmarks[muscle] };
    const over = own(custom, muscle);
    if (isObj(over)) {
      ['mev', 'mavLow', 'mavHigh', 'mrv'].forEach(k => {
        if (isNum(over[k]) && over[k] >= 0) lm[k] = over[k];
      });
    }
    out[muscle] = lm;
  });
  return out;
}

/**
 * Where a weekly set count sits against a landmark.
 * @param {number} sets
 * @param {{mev:number, mavHigh:number, mrv:number}} landmark
 * @returns {'below'|'productive'|'high'|'over'|null} below < MEV <= productive
 *   <= MAV top < high <= MRV < over; null without a number or landmark
 */
export function volumeZone(sets, landmark) {
  if (!isNum(sets) || !isObj(landmark)) return null;
  if (!isNum(landmark.mev) || !isNum(landmark.mavHigh) || !isNum(landmark.mrv)) return null;
  if (sets < landmark.mev) return 'below';
  if (sets <= landmark.mavHigh) return 'productive';
  if (sets <= landmark.mrv) return 'high';
  return 'over';
}

/* The movement an exercise name ends in, brackets and equipment words left
 * out: 'Chest Supported Row' -> 'row', 'Preacher Curl (machine)' -> 'curl',
 * 'Chin-up' -> 'up' (as 'Pull-up'). */
function movementWord(name) {
  const w = words(name, true).filter(x => !STRIPPED.has(x));
  return w.length ? w[w.length - 1] : null;
}

function sameSet(a, b) {
  return a.length === b.length && a.every(x => b.includes(x));
}

/**
 * Library exercises (plus the default-plan extras) with the same primary
 * muscle, other equipment first ("machine taken"), then by opts.counts, then
 * library order. With opts.similar, within each equipment group the same
 * movement (last word of the name: row, press, curl...) ranks first, then the
 * same secondary muscles, and only then counts: a row for a row, pull-ups
 * (not deadlifts) for a lat pulldown.
 * @param {string} name the exercise to replace
 * @param {{exclude?:string[], limit?:number, counts?:Object<string,number>,
 *   exerciseMeta?:object, similar?:boolean}} [opts] exclude: names to leave
 *   out (compared normalised); limit defaults to 3; counts: e.g. logged
 *   sessions in the last 60 days per name
 * @returns {Array<{name:string, muscle:string, equipment:string}>} [] when the
 *   name doesn't resolve
 */
export function sameMuscleAlternatives(name, opts = {}) {
  const o = isObj(opts) ? opts : {};
  const target = resolveMuscles(name, o.exerciseMeta);
  if (!target) return [];
  const limit = isNum(o.limit) && o.limit >= 0 ? Math.floor(o.limit) : 3;
  const ownEntry = target.match ? BY_NAME.get(target.match) : null;
  const equipment = equipmentWord(name) || (ownEntry ? ownEntry.equipment : null);
  const skip = new Set([softKey(name)]);
  if (target.match) skip.add(softKey(target.match));
  (Array.isArray(o.exclude) ? o.exclude : []).forEach(n => { if (typeof n === 'string') skip.add(softKey(n)); });
  const counts = isObj(o.counts) ? o.counts : {};
  const move = o.similar === true ? movementWord(name) : null;
  const seen = new Set();
  const ranked = [];
  ENTRIES.forEach((e, index) => {
    if (seen.has(e.name) || skip.has(softKey(e.name))) return;
    seen.add(e.name);
    const m = entryMuscles(e);
    if (m.primary !== target.primary) return;
    const count = own(counts, e.name);
    ranked.push({
      e, index, other: equipment === null || e.equipment !== equipment ? 1 : 0, count: isNum(count) ? count : 0,
      move: move !== null && movementWord(e.name) === move ? 1 : 0,
      second: o.similar === true && sameSet(m.secondary, target.secondary) ? 1 : 0,
    });
  });
  ranked.sort((a, b) => b.other - a.other || b.move - a.move || b.second - a.second || b.count - a.count
    || a.index - b.index);
  return ranked.slice(0, limit).map(r => ({ name: r.e.name, muscle: target.primary, equipment: r.e.equipment }));
}

/* difflib.SequenceMatcher(None, a, b, autojunk=False).ratio(), so the Python
 * twin can call difflib and land on the same number. */
function ratio(a, b) {
  const A = Array.from(a);
  const B = Array.from(b);
  if (!A.length && !B.length) return 1;
  const b2j = new Map();
  B.forEach((c, j) => {
    if (!b2j.has(c)) b2j.set(c, []);
    b2j.get(c).push(j);
  });
  let matched = 0;
  const queue = [[0, A.length, 0, B.length]];
  while (queue.length) {
    const [alo, ahi, blo, bhi] = queue.pop();
    let besti = alo;
    let bestj = blo;
    let size = 0;
    let j2len = new Map();
    for (let i = alo; i < ahi; i += 1) {
      const next = new Map();
      for (const j of b2j.get(A[i]) || []) {
        if (j < blo) continue;
        if (j >= bhi) break;
        const k = (j2len.get(j - 1) || 0) + 1;
        next.set(j, k);
        if (k > size) { besti = i - k + 1; bestj = j - k + 1; size = k; }
      }
      j2len = next;
    }
    if (!size) continue;
    matched += size;
    if (alo < besti && blo < bestj) queue.push([alo, besti, blo, bestj]);
    if (besti + size < ahi && bestj + size < bhi) queue.push([besti + size, ahi, bestj + size, bhi]);
  }
  return (2 * matched) / (A.length + B.length);
}

function median(values) {
  if (!values.length) return null;
  const v = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

function nameStats(logs) {
  const stats = new Map();
  Object.keys(logs).filter(k => dayNumber(k) !== null).sort().forEach(date => {
    const day = logs[date];
    if (!isObj(day)) return;
    Object.keys(day).forEach(name => {
      const sets = Array.isArray(day[name]) ? day[name].filter(s => isObj(s) && s.done) : [];
      if (!sets.length) return;
      if (!stats.has(name)) stats.set(name, { dates: new Set(), first: date, last: date, tops: [] });
      const st = stats.get(name);
      st.dates.add(date);
      st.last = date;
      const loads = sets.map(s => s.weight).filter(w => isNum(w) && w > 0);
      if (loads.length) st.tops.push(Math.max(...loads));
    });
  });
  return stats;
}

function summary(st) {
  const m = median(st.tops);
  return { sessions: st.dates.size, first: st.first, last: st.last, medianTop: m === null ? null : roundTo(m, 10) };
}

/**
 * Logged names that look like one exercise under two names (I39): the same
 * nameKey or a difflib ratio >= 0.85 on the normalised names, AND never
 * logged on the same date, AND median top-set loads within 35% of the larger
 * (both without loads also passes). Suggestions only; merging is Mo's call.
 * @param {object} logs state.logs
 * @returns {Array<{from:string, to:string, reason:'normalised'|'similar', similarity:number,
 *   fromStats:{sessions:number, first:string, last:string, medianTop:number|null},
 *   toStats:{sessions:number, first:string, last:string, medianTop:number|null}}>}
 *   `to` is the name logged most recently; sorted normalised first, then by
 *   similarity, then by name
 */
export function suggestAliases(logs) {
  if (!isObj(logs)) return [];
  const stats = nameStats(logs);
  const names = [...stats.keys()].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  const out = [];
  for (let i = 0; i < names.length; i += 1) {
    for (let j = i + 1; j < names.length; j += 1) {
      const a = names[i];
      const b = names[j];
      const softA = softKey(a);
      const softB = softKey(b);
      if (!softA || !softB) continue;
      const keyA = nameKey(a);
      const same = keyA !== '' && keyA === nameKey(b);
      const sim = ratio(softA, softB);
      if (!same && sim < SIMILAR) continue;
      const sa = stats.get(a);
      const sb = stats.get(b);
      if ([...sa.dates].some(d => sb.dates.has(d))) continue;
      const ma = median(sa.tops);
      const mb = median(sb.tops);
      if ((ma === null) !== (mb === null)) continue;
      if (ma !== null && Math.abs(ma - mb) > LOAD_BAND * Math.max(ma, mb)) continue;
      // The dates are disjoint, so the last dates differ.
      const [from, to] = sa.last > sb.last ? [b, a] : [a, b];
      out.push({
        from, to, reason: same ? 'normalised' : 'similar', similarity: roundTo(sim, 1000),
        fromStats: summary(stats.get(from)), toStats: summary(stats.get(to)),
      });
    }
  }
  const rank = s => (s.reason === 'normalised' ? 0 : 1);
  out.sort((x, y) => rank(x) - rank(y) || y.similarity - x.similarity
    || (x.from < y.from ? -1 : x.from > y.from ? 1 : 0) || (x.to < y.to ? -1 : x.to > y.to ? 1 : 0));
  return out;
}
